import {$, ElementDecorator, ViewTranslator} from '../util/view'
import {Banner} from './banner'
import settings from './settings'
import FrontendAPI from '../api/frontend'
import {fetchVendors, fetchNecessaryVendorList, fetchNotRegisteredVendorList, fetchDirectVendorList, NecessaryVendorList, notRegisteredVendors, VendorList, Vendor} from '../service/vendor-list'
import {PublisherPurposesList} from '../service/publisher-purposes-list'
import alert from './alert'
import {postTrackingEvent} from '../util/tracking'
import {getAndRemoveItem} from '../util/storage'
import {I18n, Translator} from '../i18n'

const JUST_CONSENTED = (() => {
  try {
    return !!getAndRemoveItem('mol.ads.cmp.alert')
  } catch (error) {
    return false
  }
})()

export interface Options {
  api: FrontendAPI,
  banner?: Banner,
  baseURL: string,
  hasChoiceBeenMade: boolean,
  publisherPurposesList: PublisherPurposesList,
  renderSettings?: boolean,
  t: ViewTranslator,
  translate: Translator
}

export type AppView = ElementDecorator & {
  alert: () => void,
  openSettings: () => void
}

export default function render ({
  api,
  banner,
  baseURL,
  hasChoiceBeenMade,
  publisherPurposesList,
  renderSettings = true,
  t,
  translate
}: Options): AppView {
  const consentAlert = alert({
    message: 'Great! Thanks for choosing the best browsing experience. You can change your privacy settings at any time from our Privacy Policy.',
    onClose: () => {
      consentAlert.remove()
    }
  })

  const dissentAlert = alert({
    message: translate('banner.alert.optOut'),
    onClose: () => {
      dissentAlert.remove()
    }
  })

  const toggleSettings = (event: MouseEvent): void => {
    event.preventDefault()
    app.raw.classList.toggle('mol-ads-cmp--settings-open')
  }

  const consentToAll = (event: MouseEvent): void => {
    event.preventDefault()
    api.consentToAll()
  }

  const dissentToAll = (event: MouseEvent): void => {
    event.preventDefault()
    api.dissentToAll()
  }

  const showConsentAlert = () => {
    app.appendChild(consentAlert)
  }

  const loadAndRenderSettings = async () => {
    if (renderSettings) {
      const [
        vendorList,
        necessaryVendorList,
        notRegisteredVendors,
        directVendorList,
        vendors
      ] = await Promise.all([
        api.callP('getVendorList'),
        fetchNecessaryVendorList(baseURL),
        fetchNotRegisteredVendorList(baseURL),
        fetchDirectVendorList(baseURL),
        fetchVendors(baseURL)
      ])

      const opts = Object.assign({}, options, {
        directVendorList,
        necessaryVendorList,
        notRegisteredVendors,
        vendorList: vendorList as VendorList,
        vendors
      })

      const settingsModal = settings(opts)
      app.appendChild(settingsModal)

      renderSettings = false
    }

  }

  const showDissentAlert = () => {
    app.appendChild(dissentAlert)
  }

  const showAlert = async (consent?: boolean) => {
    if (typeof consent === 'undefined') {
      consent = await api.callP('hasConsentedToAll') as boolean
    }

    if (consent) {
      showConsentAlert()
    } else {
      showDissentAlert()
    }
  }

  const options = {
    api,
    baseURL,
    hasChoiceBeenMade,
    onAccept: [consentToAll as EventListener],
    onCloseSettings: [toggleSettings as EventListener],
    onDecline: [dissentToAll as EventListener],
    onOpenSettings: [
      toggleSettings as EventListener,
      () => postTrackingEvent('overlay open settings'),
      async () => {
        loadAndRenderSettings()
      },
    ],
    publisherPurposesList,
    t,
    translate
  }

  const parts = banner ? [banner(options)] : []

  const shakeCookieBanner = async (e: Event) => {
    const t = e.target as HTMLElement
    const banner = document.getElementsByClassName('mol-ads-cmp')[0] as HTMLElement
    banner.style.animationName = ''
    if (t.classList.contains('mol-ads-cmp')) {
      await new Promise(r => setTimeout(r, 0))
      const animationType = location.search.match(/ac.cmp.animation=(\w+)/)
      banner.style.animationName = `${animationType ? animationType[1] : 'shake'}`
    }
  }

  const app = (
    $('div', {
      'class': 'mol-ads-cmp',
      'data-cmp-no-consent': true
    }, parts)
      .on('mousedown', shakeCookieBanner)
      .on('touchstart', shakeCookieBanner)
  )

  if (!hasChoiceBeenMade && banner) {
    postTrackingEvent('overlay displayed')
    document.documentElement.classList.add('mol-ads-cmp--open')

    api.callP('onUpdate').then(consented => {
      document.documentElement.classList.toggle('mol-ads-cmp--open')
      showAlert(consented as boolean)
    })
  } else {
    options.onAccept.push(showConsentAlert)
    options.onDecline.push(showDissentAlert)

    if (JUST_CONSENTED) {
      showAlert()
    }
  }

  return Object.assign(
    app,
    {
      alert: showAlert,

      openSettings: async () => {
        app.raw.classList.add('mol-ads-cmp--settings-open')
        postTrackingEvent('overlay open settings')

        loadAndRenderSettings()
      }
    }
  )
}
